/**
 * The how-to guides' metadata, per language: which files make up the library, and
 * each guide's title, short title and description in every language that has them.
 *
 * Kept apart from howToContent.ts, and free of imports that need a bundler, so the
 * language gate can read it at build time (lib/languageGate.ts, vite.config.ts)
 * without the guides' markdown being bundled.
 */

import type { Language } from './language';

/** The guides, language-independent: slug, source filename, library position. */
export const GUIDE_FILES = [
  { slug: 'subcutaneous-fluids', file: '01-subcutaneous-fluids.md', order: 1 },
  { slug: 'pills-and-liquids', file: '02-pills-and-liquids.md', order: 2 },
  { slug: 'transdermal-medications', file: '03-transdermal-medications.md', order: 3 },
  { slug: 'phosphate-binders', file: '04-phosphate-binders.md', order: 4 },
  { slug: 'skin-tent-hydration', file: '05-skin-tent-hydration.md', order: 5 },
  { slug: 'fecal-scoring', file: '06-fecal-scoring.md', order: 6 },
  { slug: 'weighing-at-home', file: '07-weighing-at-home.md', order: 7 },
  { slug: 'body-condition-score', file: '08-body-condition-score.md', order: 8 },
  { slug: 'water-intake-litter-box', file: '09-water-intake-litter-box.md', order: 9 },
  { slug: 'nausea-and-appetite', file: '10-nausea-and-appetite.md', order: 10 },
  { slug: 'resting-breathing-rate', file: '11-resting-breathing-rate.md', order: 11 },
] as const;

export type GuideSlug = (typeof GUIDE_FILES)[number]['slug'];

export interface GuideText {
  /** Full display title — must match the guide's own H1 in that language. */
  title: string;
  /** Compact label for inline callouts and prev/next navigation. */
  shortTitle: string;
  /** One-line summary for the library index cards. */
  description: string;
}

export interface HowToLanguageText {
  guides: Record<GuideSlug, GuideText>;
  /**
   * The guides refer to one another by title in *emphasis* (e.g. "see *Counting
   * the resting breathing rate*"). This maps those exact phrases, as they appear in
   * that language's guides, onto slugs so the renderer can turn them into router
   * links — a purely mechanical transform on text that is otherwise shipped
   * verbatim. Emphasis that isn't in this map stays plain emphasis.
   */
  crossReferences: Record<string, GuideSlug>;
}

/** Each language's guide metadata. A language is added here with its guide files. */
export const HOW_TO_TEXT: Partial<Record<Language, HowToLanguageText>> = {
  en: {
    guides: {
      'subcutaneous-fluids': {
        title: 'Giving your cat fluids under the skin (SQ fluids)',
        shortTitle: 'Giving subcutaneous fluids',
        description: 'Warming and priming the bag, placing the needle, and what to expect afterwards.',
      },
      'pills-and-liquids': {
        title: 'Giving your cat pills & liquid medicine',
        shortTitle: 'Giving pills & liquid medicine',
        description: 'Hiding a tablet in a treat, giving one by hand, and syringing a liquid.',
      },
      'transdermal-medications': {
        title: 'Applying ear (transdermal) medications',
        shortTitle: 'Applying ear (transdermal) medications',
        description: 'The right spot on the ear flap, and why gloves matter every single time.',
      },
      'phosphate-binders': {
        title: 'Giving phosphate binders with meals',
        shortTitle: 'Giving phosphate binders',
        description: 'Why binders belong in the food, how to mix them, and timing around other medicines.',
      },
      'skin-tent-hydration': {
        title: "Checking your cat’s hydration (the skin-tent test)",
        shortTitle: 'Checking hydration (skin tent)',
        description: 'How to do the skin tent, why it can mislead, and the signs to check alongside it.',
      },
      'fecal-scoring': {
        title: 'Fecal scoring at home',
        shortTitle: 'Fecal scoring at home',
        description: 'The 1–7 scale, and how a ten-second score turns the litter box into data.',
      },
      'weighing-at-home': {
        title: 'Weighing your cat at home',
        shortTitle: 'Weighing your cat at home',
        description: 'Choosing a scale, the hold-and-subtract method, and getting a number you can trust.',
      },
      'body-condition-score': {
        title: 'Body condition scoring at home',
        shortTitle: 'Body condition scoring',
        description: 'The 9-point scale, the knuckle test, and noticing the muscle loss weight can hide.',
      },
      'water-intake-litter-box': {
        title: 'Monitoring water intake & the litter box',
        shortTitle: 'Water intake & the litter box',
        description: "Watching drinking and urine clumps for a change from your cat’s own normal.",
      },
      'nausea-and-appetite': {
        title: 'Spotting nausea & appetite changes',
        shortTitle: 'Spotting nausea & appetite changes',
        description: 'The quiet cues that come before an empty bowl, and mealtime tricks that help.',
      },
      'resting-breathing-rate': {
        title: "Counting your cat’s resting breathing rate",
        shortTitle: 'Counting the resting breathing rate',
        description: 'Thirty seconds and a phone timer — how to count it and what to watch for.',
      },
    },
    crossReferences: {
      'Counting the resting breathing rate': 'resting-breathing-rate',
      'Fecal scoring at home': 'fecal-scoring',
      'Monitoring water intake & the litter box': 'water-intake-litter-box',
      'Spotting nausea & appetite changes': 'nausea-and-appetite',
      'Weighing your cat at home': 'weighing-at-home',
      'Applying ear medications': 'transdermal-medications',
    },
  },
  'pt-BR': {
    guides: {
      'subcutaneous-fluids': {
        title: 'Como dar fluidos sob a pele ao seu gato (fluidoterapia subcutânea)',
        shortTitle: 'Como dar fluidos subcutâneos',
        description: 'Como aquecer e preparar a bolsa, posicionar a agulha e o que esperar depois.',
      },
      'pills-and-liquids': {
        title: 'Como dar comprimidos e remédios líquidos ao seu gato',
        shortTitle: 'Como dar comprimidos e remédios líquidos',
        description:
          'Esconder um comprimido em um petisco, dar um comprimido com a mão e dar um líquido com seringa.',
      },
      'transdermal-medications': {
        title: 'Como aplicar medicamentos na orelha (transdérmicos)',
        shortTitle: 'Como aplicar medicamentos na orelha (transdérmicos)',
        description:
          'O local certo no pavilhão da orelha e por que as luvas importam todas as vezes, sem exceção.',
      },
      'phosphate-binders': {
        title: 'Como dar quelantes de fósforo com as refeições',
        shortTitle: 'Como dar quelantes de fósforo',
        description:
          'Por que os quelantes devem ir na comida, como misturá-los e os horários em relação a outros medicamentos.',
      },
      'skin-tent-hydration': {
        title: 'Como verificar a hidratação do seu gato (teste da prega cutânea)',
        shortTitle: 'Como verificar a hidratação (prega cutânea)',
        description:
          'Como fazer o teste da prega cutânea, por que ele pode enganar e os sinais para verificar junto com ele.',
      },
      'fecal-scoring': {
        title: 'Escore fecal em casa',
        shortTitle: 'Escore fecal em casa',
        description:
          'A escala de 1 a 7 e como um escore de dez segundos transforma a caixa de areia em dados.',
      },
      'weighing-at-home': {
        title: 'Pesando seu gato em casa',
        shortTitle: 'Pesando seu gato em casa',
        description:
          'Como escolher uma balança, o método de segurar e subtrair e como obter um número confiável.',
      },
      'body-condition-score': {
        title: 'Escore de condição corporal em casa',
        shortTitle: 'Escore de condição corporal',
        description:
          'A escala de 9 pontos, o teste dos nós dos dedos e como perceber a perda muscular que o peso pode esconder.',
      },
      'water-intake-litter-box': {
        title: 'Monitorando a ingestão de água e a caixa de areia',
        shortTitle: 'Ingestão de água e caixa de areia',
        description:
          'Observar o consumo de água e os torrões de urina em busca de mudanças em relação ao normal do seu gato.',
      },
      'nausea-and-appetite': {
        title: 'Identificando náusea e mudanças no apetite',
        shortTitle: 'Identificando náusea e mudanças no apetite',
        description:
          'Os sinais discretos que aparecem antes da tigela vazia e truques na hora da refeição que ajudam.',
      },
      'resting-breathing-rate': {
        title: 'Contando a frequência respiratória em repouso do seu gato',
        shortTitle: 'Contando a frequência respiratória em repouso',
        description: 'Trinta segundos e o timer do celular — como contar e o que observar.',
      },
    },
    crossReferences: {
      'Contando a frequência respiratória em repouso': 'resting-breathing-rate',
      'Escore fecal em casa': 'fecal-scoring',
      'Monitorando a ingestão de água e a caixa de areia': 'water-intake-litter-box',
      'Identificando náusea e mudanças no apetite': 'nausea-and-appetite',
      'Pesando seu gato em casa': 'weighing-at-home',
      'Como aplicar medicamentos na orelha': 'transdermal-medications',
    },
  },
  es: {
    guides: {
      'subcutaneous-fluids': {
        title: 'Cómo administrar fluidos bajo la piel a su gato (fluidoterapia subcutánea)',
        shortTitle: 'Cómo administrar fluidos subcutáneos',
        description: 'Cómo calentar y preparar la bolsa, colocar la aguja y qué esperar después.',
      },
      'pills-and-liquids': {
        title: 'Cómo dar pastillas y medicamentos líquidos a su gato',
        shortTitle: 'Cómo dar pastillas y medicamentos líquidos',
        description:
          'Esconder una pastilla en un premio, darla con la mano y administrar un líquido con jeringa.',
      },
      'transdermal-medications': {
        title: 'Cómo aplicar medicamentos en la oreja (transdérmicos)',
        shortTitle: 'Cómo aplicar medicamentos en la oreja (transdérmicos)',
        description:
          'El lugar correcto del pabellón auricular y por qué los guantes importan todas y cada una de las veces.',
      },
      'phosphate-binders': {
        title: 'Cómo dar quelantes de fósforo con las comidas',
        shortTitle: 'Cómo dar quelantes de fósforo',
        description:
          'Por qué los quelantes van en la comida, cómo mezclarlos y cómo organizar los horarios con otros medicamentos.',
      },
      'skin-tent-hydration': {
        title: 'Cómo comprobar la hidratación de su gato (prueba del pliegue cutáneo)',
        shortTitle: 'Cómo comprobar la hidratación (pliegue cutáneo)',
        description:
          'Cómo hacer la prueba del pliegue cutáneo, por qué puede engañar y qué otros signos comprobar a la vez.',
      },
      'fecal-scoring': {
        title: 'Puntuación fecal en casa',
        shortTitle: 'Puntuación fecal en casa',
        description:
          'La escala del 1 al 7 y cómo una puntuación de diez segundos convierte la caja de arena en datos.',
      },
      'weighing-at-home': {
        title: 'Cómo pesar a su gato en casa',
        shortTitle: 'Cómo pesar a su gato en casa',
        description:
          'Elegir una báscula, el método de sostener y restar, y obtener una cifra fiable.',
      },
      'body-condition-score': {
        title: 'Puntuación de condición corporal en casa',
        shortTitle: 'Puntuación de condición corporal',
        description:
          'La escala de 9 puntos, la prueba de los nudillos y cómo notar la pérdida muscular que el peso puede ocultar.',
      },
      'water-intake-litter-box': {
        title: 'Control del consumo de agua y de la caja de arena',
        shortTitle: 'Consumo de agua y caja de arena',
        description:
          'Vigilar lo que bebe y los terrones de orina para detectar un cambio respecto a lo normal en su gato.',
      },
      'nausea-and-appetite': {
        title: 'Cómo detectar náuseas y cambios de apetito',
        shortTitle: 'Cómo detectar náuseas y cambios de apetito',
        description:
          'Las señales discretas que aparecen antes de que su gato deje la comida sin tocar, y trucos a la hora de comer que ayudan.',
      },
      'resting-breathing-rate': {
        title: 'Cómo contar la frecuencia respiratoria en reposo de su gato',
        shortTitle: 'Cómo contar la frecuencia respiratoria en reposo',
        description:
          'Treinta segundos y el temporizador del teléfono: cómo contarla y qué vigilar.',
      },
    },
    crossReferences: {
      'Cómo contar la frecuencia respiratoria en reposo': 'resting-breathing-rate',
      'Puntuación fecal en casa': 'fecal-scoring',
      'Control del consumo de agua y de la caja de arena': 'water-intake-litter-box',
      'Cómo detectar náuseas y cambios de apetito': 'nausea-and-appetite',
      'Cómo pesar a su gato en casa': 'weighing-at-home',
      'Cómo aplicar medicamentos en la oreja': 'transdermal-medications',
    },
  },
};
